import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { StatTile } from '@/components/ui/stat-tile';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { formatMinor } from '@/hooks/use-shifts';
import {
  RETURNS_REPORT_MAX_DAYS,
  downloadReturnsCsv,
  useReturnsReport,
  useReturnsReportFilters,
  type ReturnsReportPerson,
  type ReturnsReportScope,
} from '@/hooks/use-returns-report';
import { extractApiError } from '@/lib/api-error';
import { useT } from '@/i18n/use-t';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

/** A local calendar date as YYYY-MM-DD. */
function localIso(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Local midnight of a YYYY-MM-DD date, `plusDays` later. */
function localMidnight(isoDate: string, plusDays = 0): Date {
  const [y, m, d] = isoDate.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d + plusDays);
}

/** Whole local calendar days from `from` to `to`, both included. */
function daysInclusive(from: string, to: string): number {
  return Math.round((localMidnight(to).getTime() - localMidnight(from).getTime()) / DAY_MS) + 1;
}

function person(p: ReturnsReportPerson | null): string {
  if (p === null) return '—';
  if (p.displayName !== null && p.employeeCode !== null)
    return `${p.displayName} (${p.employeeCode})`;
  return p.displayName ?? p.employeeCode ?? '—';
}

/**
 * RL-513 / TRS §22.9 — Reports → Returns. Every refund in a date range with the
 * cashier who rang it up (operator) and the one who authorised it (approver),
 * and a per-cashier summary; both downloadable as CSV.
 *
 * The scope lives in the URL (as on Turnover), so refresh, sharing and Back all
 * land on the same report. The dates are the viewer's local calendar days; the
 * server receives the instants they start and end at.
 */
export function ReturnsReportPage() {
  const t = useT('admin_ui');
  const [params, setParams] = useSearchParams();
  const today = localIso(new Date());
  const fromParam = params.get('from');
  const toParam = params.get('to');
  const fromDate =
    fromParam !== null && ISO_DATE.test(fromParam)
      ? fromParam
      : localIso(new Date(localMidnight(today, -6)));
  const toDate = toParam !== null && ISO_DATE.test(toParam) ? toParam : today;
  const locationId = params.get('locationId') ?? '';
  const terminalId = params.get('terminalId') ?? '';
  const cashierId = params.get('cashierId') ?? '';

  const update = (next: Record<string, string>): void => {
    const merged: Record<string, string> = {
      from: fromDate,
      to: toDate,
      locationId,
      terminalId,
      cashierId,
      ...next,
    };
    setParams(Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '')));
  };

  const days = daysInclusive(fromDate, toDate);
  const rangeError =
    days < 1
      ? t('reports.returns.rangeReversed', 'The end date is before the start date.')
      : days > RETURNS_REPORT_MAX_DAYS
        ? t('reports.returns.rangeTooLong', 'Choose at most {days} days.').replace(
            '{days}',
            String(RETURNS_REPORT_MAX_DAYS),
          )
        : null;

  const scope: ReturnsReportScope | null =
    rangeError !== null
      ? null
      : {
          from: localMidnight(fromDate).toISOString(),
          to: localMidnight(toDate, 1).toISOString(),
          ...(locationId ? { locationId } : {}),
          ...(terminalId ? { terminalId } : {}),
          ...(cashierId ? { cashierId } : {}),
        };

  const filters = useReturnsReportFilters();
  const { data, isLoading, error } = useReturnsReport(scope);
  const [downloading, setDownloading] = useState<'refunds' | 'cashiers' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const terminals = (filters.data?.terminals ?? []).filter(
    (term) => locationId === '' || term.locationId === locationId,
  );

  const download = async (view: 'refunds' | 'cashiers'): Promise<void> => {
    if (scope === null) return;
    setDownloading(view);
    setDownloadError(null);
    try {
      const prefix = view === 'cashiers' ? 'returns-cashiers' : 'returns';
      await downloadReturnsCsv(scope, view, `${prefix}_${fromDate}_${toDate}.csv`);
    } catch (err) {
      setDownloadError(extractApiError(err));
    } finally {
      setDownloading(null);
    }
  };

  const recordedSince =
    data !== undefined && data.approversRecordedSince !== null
      ? new Date(data.approversRecordedSince).toLocaleDateString()
      : null;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('reports.returns.title', 'Returns')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'reports.returns.intro',
            'Every refund in the chosen days, with the cashier who made it and the cashier who approved it, and totals per cashier.',
          )}
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="returns-from">{t('reports.returns.from', 'From')}</Label>
          <Input
            id="returns-from"
            type="date"
            value={fromDate}
            onChange={(e) => ISO_DATE.test(e.target.value) && update({ from: e.target.value })}
            className="max-w-[180px]"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="returns-to">{t('reports.returns.to', 'To')}</Label>
          <Input
            id="returns-to"
            type="date"
            value={toDate}
            onChange={(e) => ISO_DATE.test(e.target.value) && update({ to: e.target.value })}
            className="max-w-[180px]"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="returns-location">{t('reports.returns.location', 'Location')}</Label>
          <Select
            id="returns-location"
            value={locationId}
            onChange={(e) => update({ locationId: e.target.value, terminalId: '' })}
            className="min-w-[180px]"
          >
            <option value="">{t('reports.returns.allLocations', 'All locations')}</option>
            {(filters.data?.locations ?? []).map((l) => (
              <option key={l.id} value={l.id}>
                {l.code} · {l.name}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="returns-terminal">{t('receipts.field.terminal', 'Terminal')}</Label>
          <Select
            id="returns-terminal"
            value={terminalId}
            onChange={(e) => update({ terminalId: e.target.value })}
            className="min-w-[180px]"
          >
            <option value="">{t('reports.returns.allTerminals', 'All terminals')}</option>
            {terminals.map((term) => (
              <option key={term.id} value={term.id}>
                {term.code}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="returns-cashier">{t('receipts.field.cashier', 'Cashier')}</Label>
          <Select
            id="returns-cashier"
            value={cashierId}
            onChange={(e) => update({ cashierId: e.target.value })}
            className="min-w-[200px]"
          >
            <option value="">{t('reports.returns.allCashiers', 'All cashiers')}</option>
            {(filters.data?.cashiers ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.displayName} ({c.employeeCode})
              </option>
            ))}
          </Select>
        </div>
      </div>
      {cashierId !== '' && (
        <p className="text-sm text-muted-foreground" data-testid="returns-cashier-scope">
          {t('reports.returns.cashierScope', 'Refunds this cashier made or approved.')}
        </p>
      )}

      {rangeError !== null && (
        <p className="text-sm text-destructive" role="alert">
          {rangeError}
        </p>
      )}
      {error !== null && (
        <p className="text-sm text-destructive" role="alert">
          {extractApiError(error)}
        </p>
      )}

      {data !== undefined && (
        <section className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatTile
            label={t('reports.returns.refundCount', 'Refunds')}
            value={String(data.rows.length)}
          />
          {data.totals.map((total) => (
            <StatTile
              key={total.currency ?? ''}
              label={t('reports.returns.refundTotal', 'Refunded')}
              value={formatMinor(total.totalMinor, total.currency)}
              highlight
            />
          ))}
          <StatTile
            label={t('reports.returns.approvedByOther', 'Approved by someone else')}
            value={String(data.rows.filter((r) => r.approvedByOther).length)}
          />
        </section>
      )}

      {/* ---------- Per cashier ---------- */}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">
            {t('reports.returns.perCashier', 'Per cashier')}
          </h2>
          <Button
            variant="outline"
            size="sm"
            disabled={scope === null || downloading !== null}
            onClick={() => void download('cashiers')}
          >
            {t('reports.returns.downloadCashiers', 'Download per-cashier CSV')}
          </Button>
        </div>
        <div className="rounded-md border border-border bg-card">
          <Table>
            <THead>
              <TR>
                <TH>{t('receipts.field.cashier', 'Cashier')}</TH>
                <TH className="text-right">{t('reports.returns.operated', 'Refunds made')}</TH>
                <TH className="text-right">
                  {t('reports.returns.approvedByOther', 'Approved by someone else')}
                </TH>
                <TH className="text-right">
                  {t('reports.returns.approvedForOthers', 'Approved for others')}
                </TH>
              </TR>
            </THead>
            <TBody>
              {data?.cashiers.length === 0 && (
                <TR>
                  <TD colSpan={4} className="py-6 text-center text-muted-foreground">
                    {t('reports.returns.empty', 'No refunds in the chosen days.')}
                  </TD>
                </TR>
              )}
              {data?.cashiers.map((c) => (
                <TR
                  key={`${c.cashierId ?? `code:${c.employeeCode ?? ''}`}|${c.currency ?? ''}`}
                  data-testid="returns-cashier-row"
                >
                  <TD className="text-sm">{person(c)}</TD>
                  <TD className="text-right tabular-nums">
                    {c.refundsOperatedCount} ·{' '}
                    {formatMinor(c.refundsOperatedTotalMinor, c.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {c.approvedByOtherCount} ·{' '}
                    {formatMinor(c.approvedByOtherTotalMinor, c.currency)}
                  </TD>
                  <TD className="text-right tabular-nums">
                    {c.approvedForOthersCount} ·{' '}
                    {formatMinor(c.approvedForOthersTotalMinor, c.currency)}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </section>

      {/* ---------- Refunds ---------- */}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{t('reports.returns.refunds', 'Refunds')}</h2>
          <Button
            variant="outline"
            size="sm"
            disabled={scope === null || downloading !== null}
            onClick={() => void download('refunds')}
          >
            {t('reports.returns.downloadRefunds', 'Download refunds CSV')}
          </Button>
        </div>
        {downloadError !== null && (
          <p className="text-sm text-destructive" role="alert">
            {downloadError}
          </p>
        )}
        <div className="overflow-x-auto rounded-md border border-border bg-card">
          <Table>
            <THead>
              <TR>
                <TH>{t('receipts.field.finalised', 'Finalised')}</TH>
                <TH>{t('reports.returns.location', 'Location')}</TH>
                <TH>{t('receipts.field.terminal', 'Terminal')}</TH>
                <TH>{t('reports.returns.zReport', 'Z-report')}</TH>
                <TH>{t('reports.returns.fiscalNumber', 'Fiscal number')}</TH>
                <TH className="text-right">{t('receipts.field.total', 'Total')}</TH>
                <TH>{t('reports.returns.tender', 'Tender')}</TH>
                <TH>{t('reports.returns.reason', 'Reason')}</TH>
                <TH>{t('reports.returns.operator', 'Made by')}</TH>
                <TH>{t('receipts.detail.approvedBy', 'Approved by')}</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {isLoading && scope !== null && (
                <TR>
                  <TD colSpan={11} className="py-8 text-center text-muted-foreground">
                    {t('common.loading', 'Loading…')}
                  </TD>
                </TR>
              )}
              {data?.rows.length === 0 && (
                <TR>
                  <TD colSpan={11} className="py-8 text-center text-muted-foreground">
                    {t('reports.returns.empty', 'No refunds in the chosen days.')}
                  </TD>
                </TR>
              )}
              {data?.rows.map((row) => (
                <TR key={row.receiptId} data-testid="returns-refund-row">
                  <TD className="font-mono text-xs">
                    {new Date(row.finalisedAt).toLocaleString()}
                  </TD>
                  <TD className="text-xs">{row.locationCode ?? '—'}</TD>
                  <TD className="text-xs">{row.terminalCode}</TD>
                  <TD className="text-xs">{row.zReportRef ?? '—'}</TD>
                  <TD className="font-mono text-xs">{row.fiscalNumber ?? '—'}</TD>
                  <TD className="text-right tabular-nums">
                    {formatMinor(row.refundTotalMinor, row.currency)}
                  </TD>
                  <TD className="text-xs">{row.tender ?? '—'}</TD>
                  <TD className="text-xs">{row.returnReason ?? '—'}</TD>
                  <TD className="text-sm">{person(row.operator)}</TD>
                  <TD className="text-sm">{person(row.approver)}</TD>
                  <TD className="text-right">
                    <Link
                      to={`/receipts/${row.receiptId}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {t('receipts.action.open', 'Open')}
                    </Link>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </section>

      <p className="text-xs text-muted-foreground" data-testid="returns-approver-note">
        {recordedSince !== null
          ? t(
              'reports.returns.approverNote',
              'Approvers are recorded on refunds uploaded since {date}. An empty approver means nobody approved the refund, it was uploaded before that date, or the till did not send one.',
            ).replace('{date}', recordedSince)
          : t(
              'reports.returns.approverNoteNoDate',
              'An empty approver means nobody approved the refund, it was uploaded before Back Office recorded approvers, or the till did not send one.',
            )}
      </p>
    </div>
  );
}
